import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Description as DescriptionIcon,
  LocalShipping as ShippingIcon,
  ReceiptLong as FakturIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useInvoices } from '../hooks/useInvoices';
import { InvoiceItem } from '../services';
import PaymentButton from '../components/PaymentButton';
import { PaymentTransaction } from '../services/paymentService';
import DmsDocumentsPanel from '../components/DmsDocumentsPanel';
import { trackEvent } from '../services/analytics';
import { useTranslation } from 'react-i18next';

const getStatusColor = (status: InvoiceItem['status']): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 'DRAFT': return 'warning';
    case 'POSTED': return 'primary';
    case 'PAID': return 'success';
    case 'CLOSED': return 'success';
    case 'CANCELLED': return 'error';
    default: return 'default';
  }
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatCurrency = (amount: number, currency: string) => {
  return `${currency} ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const InvoiceDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const {
    invoices, loadInvoices, downloadInvoice, processPayment, markPaid, downloadReceipt,
    isProcessingPayment, dmsDocuments, isDmsLoading, loadDmsDocuments, downloadDmsDocument,
  } = useInvoices();
  
  const [invoice, setInvoice] = useState<InvoiceItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadInvoiceDetail = async () => {
      if (!invoiceId) {
        setError(t('errors.invoiceIdMissing'));
        setIsLoading(false);
        return;
      }

      // Try to find the invoice in the current list
      const existingInvoice = invoices.find(inv => inv.id === invoiceId);
      if (existingInvoice) {
        setInvoice(existingInvoice);
        setError(null);
        setIsLoading(false);
        // Only fire once per invoice — the effect re-runs when the
        // invoices list changes, but the id-based comparison here
        // dedupes multiple triggers for the same document.
        trackEvent('invoice_viewed', { invoice_id: invoiceId });
        return;
      }

      // If invoices haven't been loaded yet, trigger a load.
      // Don't read from the stale closure after await — instead,
      // wait for the state update to re-trigger this effect.
      if (invoices.length === 0) {
        try {
          await loadInvoices(0, 100);
          // State will update → useEffect re-runs → finds the invoice above
        } catch (err) {
          setError(t('errors.invoiceLoadFailed'));
          setIsLoading(false);
        }
        return;
      }

      // Invoices are loaded but this invoice isn't in the list
      setError(t('errors.invoiceNotFound'));
      setIsLoading(false);
    };

    loadInvoiceDetail();
  }, [invoiceId, invoices, loadInvoices]);

  // Load DMS documents when invoice is available
  useEffect(() => {
    if (invoice?.id) {
      loadDmsDocuments(invoice.id);
    }
  }, [invoice?.id, loadDmsDocuments]);

  const handleDownloadDmsDocument = async (docType: string, docIndex: number = 0) => {
    if (!invoice) return;
    try {
      await downloadDmsDocument(invoice.id, docType, docIndex);
      // Only tracked on the CUSTOMER surface. Backoffice downloads
      // (via BackofficeDmsDocumentsDialog) are internal ops and
      // stay off GA per the "no backoffice tracking" rule.
      trackEvent('dms_document_downloaded', {
        invoice_id: invoice.id,
        doc_type: docType as 'signed-invoice' | 'delivery-note' | 'faktur-pajak',
      });
    } catch (error) {
      console.error('Failed to download DMS document:', error);
    }
  };

  const handleRefreshDms = async () => {
    if (!invoice) return;
    await loadDmsDocuments(invoice.id, true);
  };

  const handleDownloadInvoice = async () => {
    if (!invoice) return;
    try {
      await downloadInvoice(invoice.id);
    } catch (error) {
      console.error('Failed to download invoice:', error);
    }
  };

  const handleProcessPayment = async () => {
    if (!invoice) return;
    try {
      const result = await processPayment(invoice.id);
      if (result && result.success && result.payment_url) {
        window.open(result.payment_url, '_blank');
      }
    } catch (error) {
      console.error('Failed to process payment:', error);
    }
  };

  const handleMarkPaid = async () => {
    if (!invoice) return;
    try {
      await markPaid(invoice.id);
      // Update local state
      setInvoice(prev => prev ? { ...prev, status: 'PAID' as const } : null);
    } catch (error) {
      console.error('Failed to mark invoice as paid:', error);
    }
  };

  const handleDownloadReceipt = async () => {
    if (!invoice) return;
    try {
      await downloadReceipt(invoice.id);
    } catch (error) {
      console.error('Failed to download receipt:', error);
    }
  };

  const handlePaymentUpdate = (paymentTransaction: PaymentTransaction) => {
    // Update invoice status based on payment status
    if (paymentTransaction.is_successful && invoice) {
      setInvoice(prev => prev ? { ...prev, status: 'PAID' as const } : null);
    }
    
    // Optionally reload invoices to get latest data
    loadInvoices();
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !invoice) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/invoices')}
          sx={{ mb: 3 }}
        >
          {t('invoices.backToInvoices')}
        </Button>
        <Alert severity="error">
          {error || 'Invoice not found'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/invoices')}
        sx={{ mb: 3 }}
      >
        {t('invoices.backToInvoices')}
      </Button>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1">
              Invoice #{invoice.invoice_number}
            </Typography>
            <Chip
              label={invoice.status}
              color={getStatusColor(invoice.status)}
              size="medium"
            />
          </Box>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" gutterBottom>
                {t('invoices.invoiceInformation')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2">
                  <strong>{t('invoices.invoiceIdLabel')}</strong> {invoice.id}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('invoices.invoiceDateLabel')}</strong> {formatDate(invoice.invoice_date)}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('invoices.dueDateLabel')}</strong> {formatDate(invoice.due_date)}
                </Typography>
                {invoice.order_number && (
                  <Typography variant="body2">
                    <strong>{t('invoices.orderNumberLabel')}</strong> #{invoice.order_number}
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" gutterBottom>
                {t('invoices.amountDetails')}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell><strong>Subtotal:</strong></TableCell>
                      <TableCell align="right">
                        {formatCurrency(invoice.total_amount - invoice.tax_amount, invoice.currency)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Tax:</strong></TableCell>
                      <TableCell align="right">
                        {formatCurrency(invoice.tax_amount, invoice.currency)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Total:</strong></TableCell>
                      <TableCell align="right">
                        <Typography variant="h6" color="primary">
                          {formatCurrency(invoice.total_amount, invoice.currency)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Aksi / Actions
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {/* Payment Button - New Midtrans Integration */}
            <Box sx={{ minWidth: '200px' }}>
              <PaymentButton
                invoice={invoice}
                onPaymentUpdate={handlePaymentUpdate}
                size="large"
                fullWidth
              />
            </Box>

            {/* Download Invoice Button */}
            {invoice.is_pdf_generated && (
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadInvoice}
                size="large"
              >
                {t('invoices.downloadInvoice')}
              </Button>
            )}
            
            {/* Legacy Payment Button (for fallback/testing) */}
            {invoice.status === 'POSTED' && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleMarkPaid}
                size="large"
              >
                {t('invoices.simulatePayment')}
              </Button>
            )}
            
            {/* Download Receipt Button */}
            {(invoice.status === 'PAID' || invoice.status === 'CLOSED') && (
              <Button
                variant="outlined"
                color="success"
                startIcon={<ReceiptIcon />}
                onClick={handleDownloadReceipt}
                size="large"
              >
                {t('invoices.downloadReceipt')}
              </Button>
            )}
          </Box>

          <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              Created: {formatDate(invoice.created_at)} •
              Last Updated: {formatDate(invoice.updated_at)}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* DMS E-Invoice Documents Section.
          Rendering delegated to the shared DmsDocumentsPanel so the
          exact same UI ships on the backoffice side (standalone
          dialog + embedded in OrderDetailDialog). The Card wrapper
          keeps the customer page's layout unchanged. Panel handles
          its own loading skeleton + "no data" empty state. */}
      {(isDmsLoading || (dmsDocuments && dmsDocuments.dms_enabled && dmsDocuments.status_documents)) && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <DmsDocumentsPanel
              dmsDocuments={dmsDocuments}
              isLoading={isDmsLoading}
              onRefresh={handleRefreshDms}
              onDownload={(docType, docIndex) => handleDownloadDmsDocument(docType, docIndex ?? 0)}
            />
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default InvoiceDetailPage;